// The hotbar tile: a forge recipe, a structure, a category door (kit-sheet
// `.tile`, screen-mock `.tile`). Three states — default, `armed`, `disabled`.
//
// **A `button`, not the sheet's `div`.** The sheet is a picture and does not
// have to be operable; a tile is the primary way a piece gets minted, so it
// wants focus and Enter/Space for free. Nothing visual changes.
//
// **Off is `aria-disabled`, never the native attribute.** A native `disabled`
// (and the `pointer-events: none` that used to go with it) suppresses the
// element's `title` — and the title is the whole point of an off tile, because
// it is what says *why* ("No foundry coalition can pay for this"). An
// explanation needs the pointer, so the element stays hoverable and focusable
// and only its `onClick` goes; the hover skew is undone by hand instead.
//
// **Armed is the one orange thing on screen**, so its wash is derived rather
// than minted: the sheet's `#3a2f14` is brand sunk into the darkest neutral,
// which is what the mix below says. The caption recolours with it through a
// custom property — `TileCaption` is a sibling component, and a descendant
// selector across two `styled()` calls needs emotion's babel plugin, which
// this build does not run.

import { styled } from "@mui/material/styles";
import { forwardRef, type ComponentPropsWithoutRef } from "react";

import { motion } from "./motion";

/** The caption's colour, set by `Tile` and read by `TileCaption`. */
const CAPTION_INK = "--kit-tile-caption";

/**
 * Two shapes, one tile. `tile` is the hotbar's 66px column with a caption
 * under the glyph; `rail` is the tool rail's 46px square (screen-mock
 * `#rail button`), glyph only, which hovers *toward the board* — the rail sits
 * on the right edge, so that is leftward, and it does not skew. Same fill,
 * edge, shadow, armed wash and off state, which is why it is a prop and not a
 * second primitive (PLAN-UI §28.2: never fork a one-off variant when a prop
 * will do).
 */
export type TileVariant = "tile" | "rail";

const TileRoot = styled("button", {
  shouldForwardProp: (prop: string) => prop !== "armed" && prop !== "variant"
})<{ armed?: boolean; variant?: TileVariant }>(({ theme, armed, variant = "tile" }) => ({
  position: "relative",
  width: variant === "rail" ? 46 : 66,
  height: variant === "rail" ? 46 : undefined,
  flex: "none",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: variant === "rail" ? "center" : undefined,
  gap: 1,
  padding: variant === "rail" ? 0 : "7px 4px 5px",
  background: armed
    ? `color-mix(in srgb, ${theme.kit.color.brand} 14%, ${theme.kit.color.ground})`
    : `color-mix(in srgb, ${theme.kit.color.panelRaised} 85%, transparent)`,
  border: `2.5px solid ${armed ? theme.kit.color.brand : theme.kit.color.line}`,
  borderRadius: variant === "rail" ? theme.kit.radius.card : theme.kit.radius.md,
  boxShadow: `0 2.5px 0 ${theme.kit.color.shadow}`,
  color: theme.kit.color.ink,
  cursor: "pointer",
  [CAPTION_INK]: armed ? theme.kit.color.brand : theme.kit.color.inkDim,
  ...motion(["transform"], ["background", "border-color"]),

  "&:hover": {
    transform: variant === "rail" ? "translateX(-4px)" : "translateX(4px) rotate(-2deg)",
    borderColor: armed ? theme.kit.color.brand : theme.kit.color.lineHi
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.kit.color.brand}`,
    outlineOffset: 2
  },
  // Off. It still hovers — it has to, to show its title — so the skew and the
  // lit border are undone here rather than by `pointer-events: none`. Same
  // specificity as `&:hover` (a class and one qualifier), so this wins by
  // coming after it.
  '&[aria-disabled="true"]': {
    opacity: 0.35,
    cursor: "default",
    transform: "none",
    borderColor: theme.kit.color.line
  },

  "@media (pointer: coarse)": { minHeight: theme.kit.touchTarget }
}));

export interface TileProps extends ComponentPropsWithoutRef<"button"> {
  /** The one lit tile — what the next click on the board will place. */
  armed?: boolean;
  /** The hotbar's captioned column, or the tool rail's square. Default `tile`. */
  variant?: TileVariant;
  /** Greyed and inert, but still hoverable: `title` says why. */
  disabled?: boolean;
}

export const Tile = forwardRef<HTMLButtonElement, TileProps>(function Tile(
  { disabled, onClick, ...rest },
  ref
) {
  return (
    <TileRoot
      {...rest}
      ref={ref}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
    />
  );
});

/**
 * The piece or structure glyph. Sized here, replaced by `Icon` at U2's end.
 * `small` is the rail's size — a 46px square holds a 19px glyph, the mock's
 * own number; an `Icon` in a rail tile is 20px for the same reason.
 */
export const TileGlyph = styled("span", {
  shouldForwardProp: (prop: string) => prop !== "small"
})<{ small?: boolean }>(({ small }) => ({
  fontSize: small ? 19 : 24,
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center"
}));

/** The price line under it — "6 silver", "token". */
export const TileCaption = styled("span")(({ theme }) => ({
  fontFamily: theme.kit.font.display,
  fontWeight: 600,
  fontSize: 10,
  fontVariantNumeric: "tabular-nums",
  color: `var(${CAPTION_INK}, ${theme.kit.color.inkDim})`
}));

/** How many of a token are held — "×1". Absent when there is nothing to count. */
export const TileCount = styled("span")(({ theme }) => ({
  position: "absolute",
  top: -7,
  right: -7,
  background: theme.kit.color.brand,
  color: theme.kit.color.onBrand,
  border: `1.5px solid ${theme.kit.color.brandDown}`,
  borderRadius: theme.kit.radius.pill,
  padding: "1px 6px",
  fontFamily: theme.kit.font.display,
  fontWeight: 700,
  fontSize: 9.5,
  fontVariantNumeric: "tabular-nums"
}));
